import { Tooltip } from "@mui/material";
import classes from "./CAVisibilityRankings.module.css";
import BrandAvatar from "../../Component/Avatar/Avatar";
import { OVERVIEW_LLM_LABELS } from "../../utils/constant";

const BADGE_CLASSES = [classes.badgeGold, classes.badgeSilver, classes.badgeBronze];
const PODIUM_CLASSES = [classes.podium1, classes.podium2, classes.podium3];

const TOOLTIP_SX = {
  bgcolor: "#1e2340",
  fontSize: "0.73rem",
  borderRadius: "10px",
  padding: "8px 12px",
  boxShadow: "0 6px 20px rgba(0,0,0,0.28)",
  "& .MuiTooltip-arrow": { color: "#1e2340" },
};

function RankBadge({ rank }) {
  const badgeClass = rank <= 3 ? BADGE_CLASSES[rank - 1] : "";
  return (
    <span className={`${classes.rankBadge} ${badgeClass}`}>{rank}</span>
  );
}

// Tooltip content showing per-LLM rank info — used in BE composite mode
function LLMSourcesTooltip({ sources, brandName }) {
  return (
    <div style={{ minWidth: 160 }}>
      {(sources || []).map((src, i) => (
        <div key={i} style={{ marginBottom: i < sources.length - 1 ? 6 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontWeight: 700, fontSize: "0.73rem", color: "#a8c4f0" }}>
              {OVERVIEW_LLM_LABELS[src.llm] || src.llm}
            </span>
            <span style={{ fontSize: "0.71rem", opacity: 0.85 }}>
              — Rank #{src.rank}
            </span>
          </div>
          {src.originalName && src.originalName !== brandName && (
            <div style={{ fontSize: "0.62rem", opacity: 0.55, fontStyle: "italic", marginTop: 2 }}>
              Listed as: "{src.originalName}"
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Named export — reused by CAVisibilityRankingsComposite.
 *
 * Props:
 *   ranking           : string[]  — ordered brand names
 *   productRankingData: object[]  — [{name, domain}] for domain lookup (FE composite / single LLM)
 *   sectionIndex      : number
 *   rankingDataItems  : object[]  — [{name, domain, sources:[{llm,rank,originalName}]}]
 *                                   from BE composite compositeProductRankingsData[setIdx] (optional)
 *                                   When provided, uses domain + sources from BE for logo and tooltip.
 */
export function CAVisibilityRankingSet({ ranking, productRankingData, sectionIndex, rankingDataItems }) {
  // BE composite mode: rankingDataItems is a parallel array to ranking (same index = same brand)
  const isBEComposite = Array.isArray(rankingDataItems) && rankingDataItems.length > 0;

  // FE composite / single-LLM mode: build domain map by name
  const domainMap = {};
  if (!isBEComposite && Array.isArray(productRankingData)) {
    productRankingData.forEach((item) => {
      if (item?.name && item?.domain) {
        domainMap[item.name.toLowerCase().trim()] = item.domain;
      }
    });
  }

  return (
    <div className={classes.setCard}>
      <div className={classes.setHeader}>
        <span className={classes.setLabel}>Set {sectionIndex + 1}</span>
      </div>
      <div className={classes.rankList}>
        {Array.isArray(ranking) && ranking.length > 0 ? (
          ranking.map((brandName, index) => {
            const rank = index + 1;
            const podiumClass = rank <= 3 ? PODIUM_CLASSES[rank - 1] : "";

            // Resolve domain and sources depending on data source
            let domain = null;
            let sources = null;

            if (isBEComposite) {
              const item = rankingDataItems[index];
              domain  = item?.domain || null;
              sources = item?.sources || null;
            } else {
              domain = domainMap[brandName?.toLowerCase()?.trim()] || null;
            }

            const brandRow = (
              <div className={`${classes.rankItem} ${podiumClass}`}>
                <RankBadge rank={rank} />
                <BrandAvatar domainName={domain || "example.com"} brandName={brandName} />
                <span className={classes.brandName}>{brandName || "—"}</span>
              </div>
            );

            // In BE composite mode, wrap each row in a tooltip showing per-LLM rank info
            if (isBEComposite && sources?.length > 0) {
              return (
                <Tooltip
                  key={brandName || index}
                  title={<LLMSourcesTooltip sources={sources} brandName={brandName} />}
                  arrow
                  placement="right"
                  componentsProps={{ tooltip: { sx: TOOLTIP_SX } }}
                >
                  {brandRow}
                </Tooltip>
              );
            }

            return (
              <div key={brandName || index}>
                {brandRow}
              </div>
            );
          })
        ) : (
          <div className={classes.emptyRow}>
            <span className={classes.emptyDash}>—</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CAVisibilityRankings({ rankings, productRankingDataSets, pIndices, sectionIndices }) {
  if (!rankings?.length) return null;

  return (
    <div className={classes.container}>
      {rankings.map((ranking, i) => (
        <CAVisibilityRankingSet
          key={pIndices ? pIndices[i] : i}
          ranking={ranking}
          productRankingData={productRankingDataSets?.[i]}
          sectionIndex={sectionIndices ? sectionIndices[i] : i}
        />
      ))}
    </div>
  );
}
